/**
 * Console Jotai store — Phase 3 (CON-02).
 *
 * Atoms backing the super-admin Console shell:
 *
 *  - `sidebarCollapsedAtom` — persists to localStorage under the LOCKED key
 *    `console:sidebar:collapsed` (do not rename; the key is part of the
 *    data contract since Plan 03-02). Toggled via Cmd-B / Ctrl-B in the
 *    sidebar; survives reload.
 *  - `saveStatusAtom` — three-state union per UI-SPEC "Save Status States".
 *    Plan 03-02 ships only the type + initial value; transition wiring
 *    (autosave debounce, retry-with-backoff) lands in Plan 03-07.
 *  - `activeCollectionSlugAtom` — set by DocumentEditPage / CollectionListPage
 *    so ConsoleTopBar can decide whether to show the SaveStatusIndicator
 *    (idle on dashboard, active on edit pages). Plan 03-04+ wires writers.
 *
 * Console-only — never imported from `src/core/marketing/` or any
 * non-console surface.
 */

import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'

/**
 * Sidebar collapsed state. LOCKED localStorage key — `console:sidebar:collapsed`.
 * Do not rename in subsequent plans (data contract).
 */
export const sidebarCollapsedAtom = atomWithStorage<boolean>(
  'console:sidebar:collapsed',
  false,
)

/**
 * Save status — exhaustive union per UI-SPEC. No fourth state.
 *
 *  - `idle` — no edit in progress (dashboard, list pages, freshly mounted edit).
 *  - `saved` — last autosave succeeded; `at` is the epoch ms timestamp.
 *  - `saving` — autosave HTTP request in flight.
 *  - `error` — autosave failed; retry countdown + attempt number drive copy.
 */
export type SaveStatus =
  | { kind: 'idle' }
  | { kind: 'saved'; at: number }
  | { kind: 'saving' }
  | { kind: 'error'; retryInSec: number; attempt: number }

export const saveStatusAtom = atom<SaveStatus>({ kind: 'idle' })

/**
 * Active collection slug. Read by ConsoleTopBar; written by CollectionListPage
 * / DocumentEditPage on mount. `null` when no collection-edit context is active
 * (e.g., dashboard, settings) — SaveStatusIndicator self-gates on saveStatus
 * but ConsoleTopBar can use this for additional UI gating later.
 */
export const activeCollectionSlugAtom = atom<string | null>(null)
